// The claim flow's data hook: drives `api.claimApprove` and maps the Worker's
// status codes to clear, owner-facing outcomes. Thin over the client — all
// request shaping lives in `api`; this only sequences the one call + holds React
// state, so it is unit-testable with a stub client (like `useMedia`).
//
// The Worker (cloud/src/lib/claim.ts) returns precise errors the camera-claim UX
// must distinguish: 404 (no such code), 409 (already approved/claimed/used — a
// single-use code), 410 (expired). We translate each into a typed reason so the
// screen can phrase it for a human, and a generic fallback covers anything else
// (incl. a network failure / 5xx).

import { useCallback, useState } from "react";
import { type ApiClient, ApiError } from "./api";
import { normalizeClaimCode } from "./claimCode";

/** Why a claim attempt failed — drives the message the screen renders. */
export type ClaimErrorReason =
  | "invalid" // 404 — the Worker doesn't know this code (typo, or never minted)
  | "expired" // 410 — the code's window lapsed (the QR is only good ~10 min)
  | "taken" // 409 — already approved/claimed by someone, or already used
  | "unauthorized" // 401/403 — the Owner Session is missing/expired (re-auth, ADR-0060)
  | "failed"; // anything else (5xx, network, malformed)

export interface ClaimError {
  reason: ClaimErrorReason;
  /** The Worker's `{ error }` message (or a fallback), for detail/debugging. */
  message: string;
}

/** The claim screen's state machine: idle → submitting → (done | error). */
export interface UseClaimState {
  /** True while the approve request is in flight. */
  submitting: boolean;
  /** Set once the claim is bound — carries the now-claimed deviceId. */
  claimedDeviceId: string | null;
  /** Set when the attempt failed, with a typed reason for the message. */
  error: ClaimError | null;
  /** Submit the approve: bind `code` to this Owner, optionally naming it. */
  approve: (code: string, name: string) => Promise<void>;
  /** Clear a prior error so the owner can retry. */
  reset: () => void;
}

/** Map an `ApiError` status to the owner-facing reason. */
function reasonFor(err: ApiError): ClaimErrorReason {
  if (err.status === 404) return "invalid";
  if (err.status === 410) return "expired";
  if (err.status === 409) return "taken";
  if (err.isUnauthorized) return "unauthorized";
  return "failed";
}

/** Drive the one-shot claim-approve call and surface a typed outcome. */
export function useClaim(client: ApiClient): UseClaimState {
  const [submitting, setSubmitting] = useState(false);
  const [claimedDeviceId, setClaimedDeviceId] = useState<string | null>(null);
  const [error, setError] = useState<ClaimError | null>(null);

  const approve = useCallback(
    async (code: string, name: string): Promise<void> => {
      if (submitting) return;
      setSubmitting(true);
      setError(null);
      try {
        const trimmedName = name.trim();
        const result = await client.claimApprove({
          // Canonicalize to XXXX-XXXX so a pasted/lowercase/hyphen-less code matches
          // the stored one instead of 404-ing (#135).
          userCode: normalizeClaimCode(code),
          // Omit an empty name — the camera stays unnamed rather than named "".
          name: trimmedName.length > 0 ? trimmedName : null,
        });
        setClaimedDeviceId(result.deviceId);
      } catch (err) {
        if (err instanceof ApiError) {
          setError({ reason: reasonFor(err), message: err.message });
        } else {
          setError({
            reason: "failed",
            message: err instanceof Error ? err.message : "claim failed",
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [client, submitting],
  );

  const reset = useCallback(() => setError(null), []);

  return { submitting, claimedDeviceId, error, approve, reset };
}
